// 🌟 Enhanced LogsTable with local-to-UTC time filter handling and zoom controls
import React, { useEffect, useState, useCallback, useMemo } from "react";
import toast from 'react-hot-toast';
import { CalendarRange, Clock, CalendarDays, History, ZoomOut, RotateCcw, User, X, Loader2, Phone, MapPin, Wifi, Calendar, Activity, Download, Plus } from "lucide-react";
import getClientConfig from '../config';
const { API_BASE_URL } = getClientConfig();

// Helper functions for future date limits
const getTwoDaysFromNow = () => {
  const date = new Date();
  date.setDate(date.getDate() + 2);
  return date.toISOString().slice(0, 16);
};

const getOneDayFromNow = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 16);
};

// Extract username from pppoe interface (strip "pppoe-" prefix)
const extractUsername = (interfaceName) => {
  if (!interfaceName) return null;
  if (interfaceName.startsWith("pppoe-")) {
    return interfaceName.substring(6); // Remove "pppoe-" prefix
  }
  return null;
};

// Format bytes to human readable
const formatBytes = (bytes) => {
  if (!bytes || bytes === "0") return "0 B";
  const num = parseInt(bytes);
  if (isNaN(num)) return bytes;
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(num) / Math.log(k));
  return parseFloat((num / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// RADIUS User Info Modal Component
function RadiusUserModal({ username, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [sourceName, setSourceName] = useState("");

  useEffect(() => {
    const fetchUserInfo = async () => {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("auth_token");

      try {
        const response = await fetch(
          `${API_BASE_URL}/radius/radius-lookup?username=${encodeURIComponent(username)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || "User not found");
        }

        const data = await response.json();
        setUserData(data.user);
        setSourceName(data.source || "");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [username]);

  // Connection status helper
  const getConnectionStatus = (status) => {
    if (status === "1") return { text: "Online", color: "bg-green-100 text-green-700" };
    return { text: "Offline", color: "bg-gray-100 text-gray-600" };
  };

  // Profile status helper
  const getProfileStatus = (status) => {
    switch (status) {
      case "1": return { text: "Active", color: "bg-green-100 text-green-700" };
      case "2": return { text: "Active", color: "bg-green-100 text-green-700" };
      case "0": return { text: "Inactive", color: "bg-red-100 text-red-600" };
      default: return { text: status || "Unknown", color: "bg-gray-100 text-gray-600" };
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{username}</h2>
              {sourceName && (
                <p className="text-blue-100 text-sm">Source: {sourceName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
              <p className="text-gray-500">Looking up user information...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <X className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-red-600 font-medium">{error}</p>
              <p className="text-gray-500 text-sm mt-1">Username: {username}</p>
            </div>
          ) : userData ? (
            <div className="space-y-6">
              {/* Status Row */}
              <div className="flex flex-wrap gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getConnectionStatus(userData.connection_status).color}`}>
                  {getConnectionStatus(userData.connection_status).text}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getProfileStatus(userData.profile_status).color}`}>
                  {getProfileStatus(userData.profile_status).text}
                </span>
                {userData.expired_status === "1" && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
                    Expired
                  </span>
                )}
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Basic Info */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-600" />
                    Basic Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Name</span>
                      <span className="font-medium text-gray-800">{userData.name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Username</span>
                      <span className="font-mono text-gray-800">{userData.username}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">NIC</span>
                      <span className="text-gray-800">{userData.nic_number || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Mobile</span>
                      <span className="text-gray-800">{userData.mobile_number || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Email</span>
                      <span className="text-gray-800">{userData.email || "-"}</span>
                    </div>
                  </div>
                </div>

                {/* Connection Info */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-blue-600" />
                    Connection Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Package ID</span>
                      <span className="font-medium text-gray-800">{userData.package_id || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">NAS ID</span>
                      <span className="text-gray-800">{userData.nas_id || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Static IP</span>
                      <span className="font-mono text-gray-800">{userData.static_ip || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">MAC Address</span>
                      <span className="font-mono text-gray-800">{userData.mac_address || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">MAC Lock</span>
                      <span className="text-gray-800">{userData.mac_lock === "1" ? "Enabled" : "Disabled"}</span>
                    </div>
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-blue-600" />
                    Usage Statistics
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Used Bandwidth</span>
                      <span className="font-medium text-gray-800">{formatBytes(userData.used_bandwidth)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Bandwidth</span>
                      <span className="text-gray-800">{userData.total_bandwidth === "0" ? "Unlimited" : formatBytes(userData.total_bandwidth)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Used Session</span>
                      <span className="text-gray-800">{userData.used_session ? `${parseFloat(userData.used_session).toFixed(1)} hrs` : "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Session</span>
                      <span className="text-gray-800">{userData.total_session === "0" ? "Unlimited" : userData.total_session}</span>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    Important Dates
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Expiration</span>
                      <span className="font-medium text-gray-800">{userData.current_expiration_date || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Last Renewed</span>
                      <span className="text-gray-800">{userData.renew_date || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Last Login</span>
                      <span className="text-gray-800">{userData.last_login_time || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Join Date</span>
                      <span className="text-gray-800">{userData.join_date || "-"}</span>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3 md:col-span-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    Location
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 block">City</span>
                      <span className="font-medium text-gray-800">
                        {userData.area_data?.city?.name || "-"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Area</span>
                      <span className="font-medium text-gray-800">
                        {userData.area_data?.area?.name || "-"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Address</span>
                      <span className="text-gray-800">{userData.address || "-"}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Box Number</span>
                      <span className="text-gray-800">{userData.box_number || "-"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LogsTable() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    src_ip: "",
    dst_ip: "",
    src_port: "",
    dst_port: "",
    asn: "",
    country: "",
    start_time: "",
    end_time: "",
    nat_public: "",
    in_iface: "",
  });

  const modalLabels = {
    timestamp: "Timestamp",
    in_iface: "User Interface", 
    out_iface: "Public Interface",
    src_ip: "Source IP",
    src_port: "Source Port",
    dst_ip: "Destination IP", 
    dst_port: "Destination Port",
    nat_public: "NAT Public",
    ptr_record: "Service/App",
    asn: "ASN",
    org: "Organization",
    country: "Country"
  };

  const [pendingFilters, setPendingFilters] = useState(filters);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [timeMode, setTimeMode] = useState("between");
  const [selectedLog, setSelectedLog] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [usedCustomTime, setUsedCustomTime] = useState(false);
  const [timeFilterMode, setTimeFilterMode] = useState("lastHour");
  const [zoomLevel, setZoomLevel] = useState(100);

  // NAT Public bulk search: list of added IPs + the input currently being typed
  const [natIpList, setNatIpList] = useState([]);
  const [natIpInput, setNatIpInput] = useState("");

  // RADIUS user modal state
  const [radiusUserModal, setRadiusUserModal] = useState({ open: false, username: "" });

  // Combine chips + whatever is typed (but not yet added) into the final
  // nat_public value. Single-IP flow works without ever pressing +.
  const buildNatPublicValue = useCallback(() => {
    const parts = [...natIpList];
    const typed = natIpInput.trim();
    if (typed && !parts.includes(typed)) parts.push(typed);
    return parts.join(",");
  }, [natIpList, natIpInput]);

  const addNatIp = () => {
    const v = natIpInput.trim();
    if (!v) return;
    if (natIpList.includes(v)) {
      setNatIpInput("");
      return;
    }
    setNatIpList([...natIpList, v]);
    setNatIpInput("");
  };

  const removeNatIp = (ip) => {
    setNatIpList(natIpList.filter((x) => x !== ip));
  };

  const isExportEnabled = useMemo(() => {
    const { src_ip, dst_ip, src_port, dst_port, asn, country, nat_public, in_iface, start_time, end_time } = filters;
    const hasFieldFilter = [src_ip, dst_ip, src_port, dst_port, asn, country, nat_public, in_iface].some(Boolean);
    const hasTimeRange = !!(start_time && end_time);
    return hasFieldFilter || hasTimeRange;
  }, [filters]);

  const handleExport = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, val]) => {
      if (val) params.append(key, val);
    });
    // Browser timezone (IANA name) — server renders the CSV's time-range
    // header rows in this zone.
    params.append("tz", Intl.DateTimeFormat().resolvedOptions().timeZone);
    try {
      const res = await fetch(`${API_BASE_URL}/logs/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `logs_export_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Export failed: " + err.message);
    }
  }, [filters]);

  const filterLabels = {
    src_ip: "Source IP",
    dst_ip: "Destination IP",
    src_port: "Source Port",
    dst_port: "Destination Port",
    asn: "Destination ASN",
    country: "Country",
    in_iface: "User Interface",
    nat_public: "NAT Public IP"
  };

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: itemsPerPage.toString(),
    });
    Object.entries(filters).forEach(([key, val]) => {
      if (val) params.append(key, val);
    });
    return params.toString();
  }, [filters, currentPage, itemsPerPage]);

  const fetchLogs = useCallback(() => {
    const token = localStorage.getItem("auth_token");
    setLoading(true);

    fetch(`${API_BASE_URL}/logs?${buildQuery()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (res.status === 401) {
          throw new Error("Unauthorized");
        }
        return res.json();
      })
      .then((data) => {
        const receivedLogs = Array.isArray(data.logs) ? data.logs : [];
        const totalCount = typeof data.total === "number" ? data.total : 0;
        setLogs(receivedLogs);
        setTotalPages(Math.ceil(totalCount / itemsPerPage));
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch logs:", err);
        setLogs([]);
        setTotalPages(1);
        setLoading(false);
      });
  }, [buildQuery, itemsPerPage]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (e) => {
    setPendingFilters({ ...pendingFilters, [e.target.name]: e.target.value });
  };

  const handleFilterSubmit = () => {
    const newFilters = { ...pendingFilters };
    const convertToRFC3339 = (localDateTime) => {
      if (!localDateTime) return "";
      return new Date(localDateTime).toISOString();
    };

    if (timeMode === "before") {
      newFilters.start_time = "";
      newFilters.end_time = convertToRFC3339(pendingFilters.end_time);
    } else if (timeMode === "after") {
      newFilters.start_time = convertToRFC3339(pendingFilters.start_time);
      newFilters.end_time = "";
    } else if (timeMode === "between") {
      const start = new Date(pendingFilters.start_time);
      const end = new Date(pendingFilters.end_time);
      if (start > end) {
        alert("End time must be after start time.");
        return;
      }
      newFilters.start_time = convertToRFC3339(pendingFilters.start_time);
      newFilters.end_time = convertToRFC3339(pendingFilters.end_time);
    }

    // nat_public comes from the chip list + input, comma-joined for the API
    newFilters.nat_public = buildNatPublicValue();

    setFilters(newFilters);
    setUsedCustomTime(true);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(50, prev - 10));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  // Handle username click - open RADIUS modal
  const handleUsernameClick = (e, interfaceName) => {
    e.stopPropagation(); // Prevent row click
    const username = extractUsername(interfaceName);
    if (username) {
      setRadiusUserModal({ open: true, username });
    } else {
      toast.error("Could not extract username from interface");
    }
  };

  const closeModal = () => setSelectedLog(null);

  if (loading)
    return (
      <div className="flex justify-center items-center h-64 text-gray-400 text-lg animate-pulse">
        Loading logs...
      </div>
    );

  return (
    <div className="px-6 py-10 space-y-10 max-w-[95rem] mx-auto font-sans">
      <div className="bg-white border border-gray-200 shadow-lg rounded-2xl p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-5 inline-flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          Time Range
        </h2>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          
        <div className="flex items-end relative mt-6 md:mt-0">
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                timeFilterMode === 'lastHour'
                  ? 'bg-blue-600 text-white shadow'
                  : 'text-gray-700 hover:bg-white'
              }`}
              onClick={() => {
                setTimeFilterMode('lastHour');
                setPendingFilters({ ...pendingFilters, start_time: '', end_time: '' });
                setUsedCustomTime(false);
                setFilters({ ...filters, start_time: '', end_time: '' });
                setCurrentPage(1);
              }}
            >
              <span className="inline-flex items-center gap-1">
                <History className="w-4 h-4" />
                Most Recent (Last 1h)
              </span>
            </button>

            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                timeFilterMode === "custom"
                  ? "bg-blue-600 text-white shadow"
                  : "text-gray-700 hover:bg-white"
              }`}
              onClick={() => {
                setTimeFilterMode("custom");
              }}
            >
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="w-4 h-4" />
                Custom Range
              </span>
            </button>
          </div>

          {timeFilterMode === "custom" && (
            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-600 font-medium ml-1">Start</label>
                <input
                  type="datetime-local"
                  name="start_time"
                  value={pendingFilters.start_time}
                  max={pendingFilters.end_time || getOneDayFromNow()}
                  onChange={handleFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-full md:w-64"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-600 font-medium ml-1">End</label>
                <input
                  type="datetime-local"
                  name="end_time"
                  value={pendingFilters.end_time}
                  max={getTwoDaysFromNow()}
                  min={pendingFilters.start_time}
                  onChange={handleFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-full md:w-64"
                />
              </div>
              <div className="flex items-end relative">
              {(!pendingFilters.start_time || !pendingFilters.end_time) && (
                <p className="absolute -top-6 text-xs text-red-500 whitespace-nowrap" style={{ left: '-10rem' }}>
    ⛔ Please select both start and end time
  </p>
)}
                <button
                  onClick={() => {
                    const start = new Date(pendingFilters.start_time);
                    const end = new Date(pendingFilters.end_time);
                    const oneDayFromNow = new Date();
                    oneDayFromNow.setDate(oneDayFromNow.getDate() + 1);
                    const twoDaysFromNow = new Date();
                    twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2);

                    if (!pendingFilters.start_time || !pendingFilters.end_time) {
                      toast.error("Both start and end times must be selected.");
                      return;
                    }

                    if (start > oneDayFromNow) {
                      toast.error("Start time cannot be more than 1 day in the future.");
                      return;
                    }

                    if (end > twoDaysFromNow) {
                      toast.error("End time cannot be more than 2 days in the future.");
                      return;
                    }

                    if (start > end) {
                      toast.error("Start time cannot be after end time.");
                      return;
                    }

                    const newFilters = {
                      ...filters,
                      start_time: start.toISOString(),
                      end_time: end.toISOString(),
                    };
                    setFilters(newFilters);
                    setUsedCustomTime(true);
                    setCurrentPage(1);
                    toast.success("Time filter applied.");
                  }}
                  disabled={!pendingFilters.start_time || !pendingFilters.end_time}

                  className={`px-5 py-2 text-sm font-semibold rounded-lg transition w-full md:w-auto
                    ${
                      pendingFilters.start_time && pendingFilters.end_time
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  title={!pendingFilters.start_time || !pendingFilters.end_time ? "Please select both start and end time to apply filter" : "Apply time range filter"}
                >
                  Apply
                </button>
                
              </div>
            </div>
          )}
        </div>
      </div>

  {/* 🎛️ Filters */}
  <div className="bg-white border border-gray-200 shadow-lg rounded-2xl p-6">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-2xl font-semibold text-gray-800 tracking-tight">Filters</h2>
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="text-sm px-3 py-1 rounded-lg border bg-gray-50 hover:bg-gray-100 transition"
      >
        {showFilters ? "Hide Filters ▲" : "Show Filters ▼"}
      </button>
    </div>

    {showFilters && (
      <div className="space-y-6 animate-fade-in">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {Object.keys(filters)
            .filter((k) => !["start_time", "end_time", "nat_public"].includes(k))
            .map((key) => (
              <div key={key} className="flex flex-col">
                <label className="text-sm font-medium text-gray-600 mb-1">
  {filterLabels[key] || key.replace(/_/g, " ")}
</label>
                <input
                  type="text"
                  name={key}
                  value={pendingFilters[key]}
                  onChange={handleFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                />
              </div>
            ))}

          {/* NAT Public IP — supports multiple IPs (bulk search) */}
          <div className="flex flex-col sm:col-span-2">
            <label className="text-sm font-medium text-gray-600 mb-1">
              NAT Public IP(s)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={natIpInput}
                onChange={(e) => setNatIpInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addNatIp();
                  }
                }}
                placeholder="e.g. 203.0.113.10"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
              />
              <button
                onClick={addNatIp}
                disabled={!natIpInput.trim()}
                title="Add another IP to search"
                className={`px-3 py-2 rounded-lg border transition inline-flex items-center
                  ${natIpInput.trim()
                    ? "text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-300"
                    : "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed"}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {natIpList.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {natIpList.map((ip) => (
                  <span
                    key={ip}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full font-mono"
                  >
                    {ip}
                    <button
                      onClick={() => removeNatIp(ip)}
                      title={`Remove ${ip}`}
                      className="hover:text-blue-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

        <div className="flex flex-col items-start space-y-1">
  {/* Time validation message */}
  {timeFilterMode === "custom" && (!pendingFilters.start_time || !pendingFilters.end_time) && (
    <p className="text-xs text-red-500" style={{ paddingLeft: '0.25rem' }}>
      ⛔ Please select both start and end time
    </p>
  )}

  <button
    onClick={() => {
      if (timeFilterMode === "custom" && (!pendingFilters.start_time || !pendingFilters.end_time)) {
        toast.error("Please select both start and end time.");
        return;
      }
      handleFilterSubmit();
    }}
    className={`inline-flex items-center gap-1 px-5 py-2 text-sm font-medium rounded-lg transition ${
      timeFilterMode === "custom" && (!pendingFilters.start_time || !pendingFilters.end_time)
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : "bg-blue-600 text-white hover:bg-blue-700"
    }`}
    disabled={timeFilterMode === "custom" && (!pendingFilters.start_time || !pendingFilters.end_time)}
  >
    🔍 Apply
  </button>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Rows per page:</label>
            <select
              className="px-2 py-1 border rounded-lg text-sm"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[10, 30, 50, 100, 200, 500].map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    )}
  </div>

      {/* 🔍 Zoom Controls */}
      <div className="bg-white border border-gray-200 shadow-lg rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Table View</h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">Zoom: {zoomLevel}%</span>
            <div className="flex gap-2">
              <button
                onClick={handleZoomOut}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition"
                title="Zoom out to see more columns"
              >
                <ZoomOut className="w-4 h-4" />
                Zoom Out
              </button>
              <button
                onClick={handleResetZoom}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition"
                title="Reset zoom to 100%"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
              <button
                onClick={handleExport}
                disabled={!isExportEnabled}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border transition
                  ${isExportEnabled
                    ? "text-green-700 bg-green-50 hover:bg-green-100 border-green-300"
                    : "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed"}`}
                title={isExportEnabled ? "Export filtered logs as CSV" : "Apply at least one filter to enable export"}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 shadow-xl rounded-2xl">
        <div className="overflow-x-auto">
          <div style={{ zoom: `${zoomLevel}%`, transformOrigin: 'top left' }}>
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-100 text-gray-600 border-b text-sm uppercase">
              <tr>
                  <th className="px-4 py-3 text-left">ID</th>
                  <th className="px-4 py-3 text-left">Timestamp</th>
                  <th className="px-4 py-3 text-left">User Interface</th>
                  <th className="px-4 py-3 text-left">Public Interface</th>
                  <th className="px-4 py-3 text-left">Src IP</th>
                  <th className="px-4 py-3 text-left">Src Port</th>
                  <th className="px-4 py-3 text-left">Dst IP</th>
                  <th className="px-4 py-3 text-left">Dst Port</th>
                  <th className="px-4 py-3 text-left">Nat Public</th>
                  <th className="px-4 py-3 text-left">Service/App</th>
                  <th className="px-4 py-3 text-left">ASN</th>
                  <th className="px-4 py-3 text-left">ORG</th>
                  <th className="px-4 py-3 text-left">Country</th>
                  <th className="px-4 py-3 text-left">Raw Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.length === 0 ? (
                  <tr>
                    <td
                      className="px-4 py-4 text-center text-gray-400"
                      colSpan={14}
                    >
                      No logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className="hover:bg-gray-100 cursor-pointer"
                    >
                      <td className="px-4 py-2">
                        <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                          {log.id}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {extractUsername(log.in_iface) ? (
                          <button
                            onClick={(e) => handleUsernameClick(e, log.in_iface)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium flex items-center gap-1"
                            title="Click to view user details"
                          >
                            <User className="w-3 h-3" />
                            {log.in_iface}
                          </button>
                        ) : (
                          log.in_iface
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {log.out_iface}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {log.src_ip}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {log.src_port}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {log.dst_ip}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {log.dst_port}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">{log.nat_public}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{log.ptr_record}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{log.asn}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{log.org}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {log.country}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[400px]">
                        {log.raw_message}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center pt-6">
        <button
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow disabled:opacity-50 hover:bg-gray-50"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ⬅ Previous
        </button>
        <span className="text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow disabled:opacity-50 hover:bg-gray-50"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next ➡
        </button>
      </div>

      {/* Log Details Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">
              Log Details (ID: {selectedLog.id})
            </h2>
            <div className="mb-4">
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-gray-800 text-[13px] font-medium leading-snug whitespace-pre-wrap break-words">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  Raw Message
                </div>
                {selectedLog.raw_message}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {Object.entries(selectedLog)
                .filter(([key]) => key !== "raw_message" && key !== "id")
                .map(([key, value]) => (
                  <div
                    key={key}
                    className="bg-gray-50 border border-gray-200 p-4 rounded-lg h-full"
                  >
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                    {modalLabels[key] || key.replace(/_/g, " ")}
                    </div>
                    <div className="text-gray-800 break-words text-[13px] font-medium leading-snug whitespace-pre-line">
                      {value}
                    </div>
                  </div>
                ))}
            </div>
            <div className="mt-8 text-right">
              <button
                onClick={closeModal}
                className="px-5 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RADIUS User Modal */}
      {radiusUserModal.open && (
        <RadiusUserModal
          username={radiusUserModal.username}
          onClose={() => setRadiusUserModal({ open: false, username: "" })}
        />
      )}
    </div>
  );
}